import React, { useState, useEffect } from 'react';
import { FiSettings, FiUser, FiCheck, FiAward, FiInfo, FiUploadCloud, FiImage } from 'react-icons/fi';
import AuthService from '../services/AuthService';
import instance from '../services/AxiosServices';

const Settings = () => {
  const user = AuthService.getCurrentUser() || { name: 'Picvot Partner', email: 'picvot.contact@gmail.com' };
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [brand, setBrand] = useState({
    name: '',
    websiteUrl: '',
    shortDescription: '',
    logoUrl: '',
    coverUrl: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await AuthService.getPartnerProfile();
      if (res && res.success) {
        setPartnerProfile(res.partner);
        if (res.brand) {
          setBrand({
            name: res.brand.name || '',
            websiteUrl: res.brand.websiteUrl || '',
            shortDescription: res.brand.shortDescription || res.brand.about || '',
            logoUrl: res.brand.logoUrl || '',
            coverUrl: res.brand.coverUrl || ''
          });
        }
      }
    } catch (e) {
      console.error('Error fetching partner profile:', e);
    } finally {
      setLoading(false);
    }
  };

  const partnerNameStr = typeof user.name === 'object'
    ? `${user.name?.en || user.name?.first || ''} ${user.name?.last || ''}`.trim()
    : (user.name || 'Partner Account');

  const handleFileUpload = async (file, targetType) => {
    if (!file) return;

    const data = new FormData();
    data.append('media', file);

    if (targetType === 'logo') setUploadingLogo(true);
    if (targetType === 'cover') setUploadingCover(true);

    try {
      const res = await instance.post('/media/partner/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedUrl = res?.url || res?.data?.url || res?.mediaUrl || res?.path;

      if (uploadedUrl) {
        if (targetType === 'logo') {
          setBrand(prev => ({ ...prev, logoUrl: uploadedUrl }));
        } else if (targetType === 'cover') {
          setBrand(prev => ({ ...prev, coverUrl: uploadedUrl }));
        }
        setMessage(`${targetType === 'logo' ? 'Logo' : 'Cover image'} uploaded successfully!`);
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setErrorMessage('Failed to upload image. Please try again.');
      setTimeout(() => setErrorMessage(''), 4000);
    } finally {
      if (targetType === 'logo') setUploadingLogo(false);
      if (targetType === 'cover') setUploadingCover(false);
    }
  };

  const handleSaveBrandProfile = async () => {
    setSaving(true);
    setMessage('');
    setErrorMessage('');
    try {
      const res = await AuthService.updatePartnerProfile({
        name: brand.name || partnerNameStr,
        websiteUrl: brand.websiteUrl,
        shortDescription: brand.shortDescription,
        logoUrl: brand.logoUrl,
        coverUrl: brand.coverUrl,
      });

      if (res && res.success) {
        setMessage('Brand Profile Saved Successfully!');
        if (res.brand) {
          setBrand({
            name: res.brand.name || '',
            websiteUrl: res.brand.websiteUrl || '',
            shortDescription: res.brand.shortDescription || res.brand.about || '',
            logoUrl: res.brand.logoUrl || '',
            coverUrl: res.brand.coverUrl || ''
          });
        }
        setTimeout(() => setMessage(''), 4000);
      } else {
        setErrorMessage(res?.message || 'Failed to save brand profile.');
      }
    } catch (err) {
      console.error('Error saving brand profile:', err);
      setErrorMessage(err.response?.data?.message || 'Failed to save brand profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 text-left pb-20">
      <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <h1 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
          <FiSettings className="text-purple-700" /> Partner Account & Brand Profile
        </h1>
        <p className="text-xs font-medium text-slate-500 mt-1">Manage partner profile, linked brand profile, logos & organization info</p>
      </div>

      {message && (
        <div className="p-4 rounded-xl bg-purple-50 border border-purple-200 text-purple-900 text-xs flex items-center gap-2 font-bold shadow-xs">
          <FiInfo className="w-4 h-4 text-purple-700" />
          <span>{message}</span>
        </div>
      )}

      {errorMessage && (
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2 font-bold shadow-xs">
          <FiInfo className="w-4 h-4 text-rose-600" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Organization Account Card */}
      <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-6 shadow-sm">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 border-b border-slate-200 pb-3">
          <FiUser className="text-purple-700" /> Partner Account Info
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">Contact Name</label>
            <input
              type="text"
              value={partnerNameStr}
              readOnly
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-900 outline-none font-bold"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">Partner Email Address</label>
            <input
              type="email"
              value={user.email || 'partner@tapcoe.com'}
              disabled
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-500 font-mono"
            />
          </div>
        </div>
      </div>

      {/* Linked Brand Profile Card */}
      <div className="bg-white border border-slate-200 p-6 rounded-2xl space-y-6 shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 pb-3">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <FiAward className="text-rose-600" /> Linked Brand Profile
          </h3>
          <span className="text-[10px] bg-rose-50 text-rose-800 border border-rose-200 font-extrabold px-2.5 py-1 rounded-full uppercase">
            Auto Created Brand
          </span>
        </div>

        {loading ? (
          <div className="py-8 text-center text-xs text-slate-500 font-medium">Loading Brand Profile...</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Brand Name</label>
                <input
                  type="text"
                  value={brand.name || partnerNameStr}
                  onChange={(e) => setBrand({ ...brand, name: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Website URL</label>
                <input
                  type="text"
                  value={brand.websiteUrl || ''}
                  onChange={(e) => setBrand({ ...brand, websiteUrl: e.target.value })}
                  placeholder="https://brand.com"
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Short Bio / Description</label>
              <textarea
                rows={2}
                value={brand.shortDescription || ''}
                onChange={(e) => setBrand({ ...brand, shortDescription: e.target.value })}
                placeholder="Official Event & Contest Organizer Brand Profile"
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
              />
            </div>

            {/* Logo and Cover Image Upload & Live Preview Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
              {/* Logo Upload & Preview */}
              <div className="space-y-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <label className="block text-xs font-bold text-slate-700">Brand Logo Image</label>
                
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={brand.logoUrl || ''}
                    onChange={(e) => setBrand({ ...brand, logoUrl: e.target.value })}
                    placeholder="https://..."
                    className="flex-1 bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-mono"
                  />
                  
                  <label className="px-3 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1 shrink-0 shadow-sm">
                    <FiUploadCloud />
                    <span>{uploadingLogo ? 'Uploading...' : 'Upload Logo'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingLogo}
                      onChange={(e) => handleFileUpload(e.target.files[0], 'logo')}
                    />
                  </label>
                </div>

                {/* Logo Live Preview */}
                <div className="pt-2">
                  <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Live Logo Preview</span>
                  {brand.logoUrl ? (
                    <div className="w-20 h-20 rounded-2xl overflow-hidden border border-purple-200 bg-white shadow-xs p-1">
                      <img
                        src={brand.logoUrl}
                        alt="Logo preview"
                        className="w-full h-full object-cover rounded-xl"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Logo'; }}
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-2xl border border-dashed border-slate-300 bg-white flex flex-col items-center justify-center text-slate-400">
                      <FiImage className="w-6 h-6 mb-1 text-slate-300" />
                      <span className="text-[9px] font-bold">No Logo</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Cover Image Upload & Preview */}
              <div className="space-y-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                <label className="block text-xs font-bold text-slate-700">Brand Cover Banner Image</label>
                
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={brand.coverUrl || ''}
                    onChange={(e) => setBrand({ ...brand, coverUrl: e.target.value })}
                    placeholder="https://..."
                    className="flex-1 bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-mono"
                  />
                  
                  <label className="px-3 py-2.5 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1 shrink-0 shadow-sm">
                    <FiUploadCloud />
                    <span>{uploadingCover ? 'Uploading...' : 'Upload Cover'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingCover}
                      onChange={(e) => handleFileUpload(e.target.files[0], 'cover')}
                    />
                  </label>
                </div>

                {/* Cover Image Live Preview */}
                <div className="pt-2">
                  <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Live Cover Preview</span>
                  {brand.coverUrl ? (
                    <div className="w-full h-24 rounded-xl overflow-hidden border border-indigo-200 bg-white shadow-xs">
                      <img
                        src={brand.coverUrl}
                        alt="Cover preview"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/600x200?text=Cover+Banner'; }}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-24 rounded-xl border border-dashed border-slate-300 bg-white flex flex-col items-center justify-center text-slate-400">
                      <FiImage className="w-6 h-6 mb-1 text-slate-300" />
                      <span className="text-[9px] font-bold">No Cover Image</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-end">
              <button
                onClick={handleSaveBrandProfile}
                disabled={saving}
                className="px-6 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 disabled:opacity-50 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-2"
              >
                <FiCheck className="w-4 h-4" /> {saving ? 'Saving Profile...' : 'Save Brand Profile'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
